import { useCallback, useRef } from 'react';

import { calculateServiceFee, getServiceFeeTiers } from 'Services/privateApis';

import type { ServiceFeeCalculateResponse, ServiceFeeTiersResponse } from '../../../../server/views/serviceFees/serviceFees.types';

type ServiceFeeDataResult = {
  fee?: ServiceFeeCalculateResponse;
  tiers?: ServiceFeeTiersResponse;
};

type UseServiceFeeDataOptions = {
  onError?: () => void;
  onSuccess?: (result: ServiceFeeDataResult) => void;
};

const useServiceFeeData = ({ onError, onSuccess }: UseServiceFeeDataOptions = {}) => {
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  const fetchServiceFeeData = useCallback(
    (price?: number, dpEnquiryId?: number) => {
      if (!price || !dpEnquiryId) {
        onErrorRef.current?.();
        return;
      }

      Promise.allSettled([
        calculateServiceFee({ dpEnquiryId, price }),
        getServiceFeeTiers(),
      ]).then(([feeResult, tiersResult]) => {
        const fee = feeResult.status === 'fulfilled' ? feeResult.value as ServiceFeeCalculateResponse : undefined;
        const tiers = tiersResult.status === 'fulfilled' ? tiersResult.value as ServiceFeeTiersResponse : undefined;

        if (fee || tiers) {
          onSuccessRef.current?.({ fee, tiers });
        } else {
          onErrorRef.current?.();
        }
      });
    },
    [],
  );

  return { fetchServiceFeeData };
};

export {
  useServiceFeeData,
  type ServiceFeeCalculateResponse,
  type ServiceFeeTiersResponse,
};
